/* ============================================================================
   ALIA booking widget
   ----------------------------------------------------------------------------
   · Green disc launcher, white "?" that flips to "×"
   · Panel: "Three ways in / Which one are you here for?"
   · Corporate lead form, opened from the panel AND from every data-act="corp"
     button out on the page

   Shadow-DOM isolated. Colours come from the theme's own tokens
   (assets/alia-tokens.css) with literal fallbacks, so the widget still renders
   correctly if that stylesheet ever fails to load.
   ========================================================================== */
(function () {
  'use strict';
  if (document.getElementById('alia-wgt-host')) return;

  /* ------------------------------------------------------------------
     CONFIG

     The endpoint, Site and domain come from window.ALIA_CFG, which
     layout/theme.liquid emits INLINE in <head> out of theme settings. Inline
     and not deferred, so it exists before this script runs.

     TRAP A: the schema default is only a default. A value saved in the theme
     editor lands in config/settings_data.json and wins permanently. If leads
     ever arrive somewhere unexpected, look in Theme settings first — not here,
     and not in settings_schema.json.

     There is deliberately NO email address in this file. The client's rule is
     no email anywhere on the site, including as a mailto fallback. A failed
     post shows the phone number instead.
     ------------------------------------------------------------------ */
  var C = window.ALIA_CFG || {};
  var CFG = {
    phone: '(514) 566-2542',
    phoneHref: 'tel:+15145662542',
    openTableUrl: 'https://www.opentable.ca/r/alia-montreal',

    // Shared ALIA Leads sheet, tab "Leads".
    endpoint: C.endpoint || '',
    // Column "Site" — identifies which ALIA site the lead came from.
    // TRAP D: this is the Shopify STORE NAME. A store left on its default name
    // makes every one of its leads unattributable in the shared sheet.
    site: C.site || C.domain || location.hostname,

    // Launcher stays hidden until the theme's splash screen clears. #pre is this
    // theme's preloader (it gains .gone when the intro finishes); the rest are
    // here so the file drops into the other ALIA themes unchanged.
    preloaderSelector: '#pre,#preloader,#intro,[id*="preload" i],[class*="preloader" i]',
    // 0 = the launcher starts appearing on the same frame the splash starts
    // leaving. #pre fades over .7s while the launcher fades in over .3s, so
    // the two cross-fade and the widget is simply there when the splash is.
    // Any value above 0 reads as the widget arriving late.
    revealDelay: 0,
    revealTimeout: 9000
  };

  if (window.ALIA_CONFIG) {
    for (var k in window.ALIA_CONFIG) {
      if (Object.prototype.hasOwnProperty.call(window.ALIA_CONFIG, k)) CFG[k] = window.ALIA_CONFIG[k];
    }
  }

  var CSS = `
  :host{
    --char:var(--alia-char,#241A11);
    --card:#241A11;
    --card-2:#2E2216;
    --ink:#EEEDE3;
    --muted:#AC9E8C;
    --faint:#8A7E6C;
    --amber:#8A5F3A;
    --rim:#9C8046;
    --line:rgba(238,237,227,.12);
    --line-2:rgba(238,237,227,.20);
    --green:#1FA35B;
    --green-lit:#2FC470;
    --green-deep:#17824A;
    --green-ink:#04210F;
    --well:#3A2A1C;
    --well-hover:#453324;
    --well-focus:#4E3B2A;
    --placeholder:#8A7E6C;
    --ember:#C4713F;
    --ember-lit:#E0705E;
    --ff:'Chivo',system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
    --mono:'Chivo Mono',ui-monospace,"SF Mono",Menlo,Consolas,monospace;
    --ease:cubic-bezier(.22,1,.36,1);
    position:fixed; inset:0; z-index:2147483000; pointer-events:none;
    font-family:var(--ff); line-height:1.6;
  }
  *{box-sizing:border-box;margin:0}
  button{font-family:inherit}
  [hidden]{display:none !important}
  #stage{position:fixed;inset:0;pointer-events:none}
  #stage a,#stage button,#stage .panel,#stage .scrim{pointer-events:auto}

  /* ---- launcher: green disc, white "?" ---- */
  .fab{position:absolute;right:clamp(16px,3vw,30px);bottom:clamp(16px,3vw,30px);
    width:58px;height:58px;border-radius:50%;border:1px solid var(--green-lit);
    background:var(--green);color:#FFFFFF;font-family:var(--ff);font-size:27px;font-weight:700;line-height:1;
    cursor:pointer;display:grid;place-items:center;
    box-shadow:0 10px 34px -8px rgba(31,163,91,.85);
    transition:transform .2s var(--ease),background .2s ease,opacity .3s ease}
  .fab:hover{background:var(--green-lit);transform:scale(1.06)}
  .fab:focus-visible{outline:2px solid #FFFFFF;outline-offset:3px}
  .fab::before{content:"";position:absolute;inset:-7px;border-radius:50%;border:1px solid var(--green);opacity:0}
  #stage[data-ready="1"] .fab::before{animation:halo 3.4s ease-in-out .6s infinite}
  @keyframes halo{0%,100%{transform:scale(1);opacity:.45}50%{transform:scale(1.12);opacity:0}}
  #stage[data-ready="0"] .fab{opacity:0;transform:scale(.6);pointer-events:none}

  /* ---- panel ---- */
  .panel{position:absolute;right:clamp(16px,3vw,30px);bottom:calc(clamp(16px,3vw,30px) + 70px);
    width:min(330px,calc(100vw - 32px));
    background-color:var(--card);
    background-image:linear-gradient(180deg,rgba(31,163,91,.07),transparent 60%);
    border:1px solid var(--line);border-top:1px solid var(--amber);border-radius:0;
    padding:22px;display:flex;flex-direction:column;gap:8px;
    box-shadow:0 26px 60px -18px rgba(0,0,0,.9);
    transform-origin:bottom right;
    transition:opacity .28s ease,transform .38s var(--ease),visibility 0s}
  #stage[data-open="0"] .panel{opacity:0;transform:scale(.9) translateY(8px);visibility:hidden;pointer-events:none;
    transition:opacity .28s ease,transform .38s var(--ease),visibility 0s linear .38s}
  .panel-t{font-size:1.1rem;font-weight:400;letter-spacing:-.01em}
  .panel-s{margin:0 0 8px;font-size:13.5px;color:var(--muted)}
  .panel-note{margin:10px 0 0;font-family:var(--mono);font-size:10.5px;letter-spacing:.12em;
    text-transform:uppercase;color:var(--amber)}
  .panel-note a{color:inherit;text-decoration:none;border-bottom:1px solid rgba(138,95,58,.6)}
  .panel-note a:hover{color:var(--ink)}

  /* ---- buttons ---- */
  .btn{font-family:var(--mono);font-size:11.5px;letter-spacing:.16em;text-transform:uppercase;
    text-decoration:none;white-space:nowrap;border-radius:0;padding:12px 16px;cursor:pointer;
    display:inline-flex;align-items:center;justify-content:center;gap:8px;width:100%;
    border:1px solid transparent;
    transition:background .18s ease,border-color .18s ease,color .18s ease,box-shadow .22s ease,transform .18s ease}
  .btn:active{transform:translateY(1px)}
  .btn:focus-visible{outline:2px solid var(--green-lit);outline-offset:3px}
  .btn-primary{background:var(--green);color:#FFFFFF;border-color:var(--green);font-weight:700}
  .btn-primary:hover{background:var(--green-deep);border-color:var(--green-deep);
    box-shadow:0 6px 26px -8px rgba(31,163,91,.8)}
  .btn-ghost{background:transparent;color:var(--green-lit);border-color:var(--green-deep)}
  .btn-ghost:hover{background:rgba(31,163,91,.12);border-color:var(--green);color:var(--green-lit)}
  .btn[disabled]{opacity:.6;cursor:progress}

  /* ---- corporate dialog ---- */
  .scrim{position:fixed;inset:0;background:rgba(8,6,4,.82);backdrop-filter:blur(4px);
    display:none;align-items:center;justify-content:center;padding:14px;z-index:900;overflow-y:auto}
  .scrim.on{display:flex}
  .dlg{position:relative;width:min(660px,100%);margin:auto;
    background-color:var(--card);
    border:1px solid var(--line);border-top:2px solid var(--green);border-radius:0;
    color:var(--ink);box-shadow:0 40px 90px -24px rgba(0,0,0,.95);
    /* No max-height and no inner scroll: the dialog grows to its content and
       the scrim scrolls. Capping the dialog and scrolling .dlg-body produced a
       second scrollbar inside the first. margin:auto (above) is what keeps a
       taller-than-viewport dialog from having its top clipped by the scrim's
       flex centring. */
    display:flex;flex-direction:column;
    animation:rise .28s var(--ease)}
  @keyframes rise{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
  .dlg-head{flex:none;display:flex;justify-content:space-between;align-items:flex-start;gap:20px;
    padding:26px 28px 20px;border-bottom:1px solid var(--line)}
  .eyebrow{font-family:var(--mono);font-size:11px;letter-spacing:.28em;text-transform:uppercase;color:var(--amber)}
  .dlg-title{font-size:1.6rem;font-weight:300;letter-spacing:-.02em;margin:6px 0;line-height:1.15}
  .dlg-sub{margin:0;font-size:14px;color:var(--muted);max-width:46ch}
  .x{flex:none;width:34px;height:34px;border-radius:0;background:transparent;border:1px solid var(--line-2);
    color:var(--muted);font-size:17px;line-height:1;cursor:pointer;transition:color .15s ease,border-color .15s ease}
  .x:hover{color:var(--ink);border-color:var(--amber)}
  .dlg-body{flex:1 1 auto;min-height:0;padding:24px 28px 28px}

  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:15px}
  .span2{grid-column:1 / -1}
  .field{display:flex;flex-direction:column;gap:7px;min-width:0}
  .field > label{font-family:var(--mono);font-size:10.5px;letter-spacing:.2em;text-transform:uppercase;color:var(--amber)}
  .field .opt{color:var(--faint)}

  /* The well is LIGHTER than the card behind it, so it reads as a surface to
     write on rather than a hole. The rim carries the boundary at 4.54:1. */
  .field input,.field select,.field textarea{
    font-family:var(--ff);font-size:16px;   /* 16px stops iOS zooming on focus */
    color:var(--ink);background:var(--well);border:0;border-radius:0;padding:13px 14px;width:100%;
    box-shadow:inset 0 0 0 1px var(--rim),0 1px 0 rgba(0,0,0,.5),0 4px 12px -6px rgba(0,0,0,.7);
    transition:background .16s ease,box-shadow .16s ease}
  .field textarea{resize:vertical;min-height:88px;font-size:15px;line-height:1.55}
  .field input:hover,.field select:hover,.field textarea:hover{background:var(--well-hover)}
  .field input:focus,.field select:focus,.field textarea:focus{
    outline:none;background:var(--well-focus);
    box-shadow:inset 0 0 0 2px var(--green-lit),0 0 0 4px rgba(31,163,91,.18)}
  /* 3.46:1 on the well AND 3.38:1 against the value colour, so an empty field
     never reads as a filled one. */
  .field input::placeholder,.field textarea::placeholder{color:var(--placeholder);opacity:1}
  /* An empty date input paints its UA "yyyy-mm-dd" hint in the full text
     colour, and an unchosen select its option — both make the one blank
     required field look like the most complete on the form. */
  .field:not(.is-filled) input[type="date"],
  .field:not(.is-filled) select{color:var(--placeholder)}
  .field input[type="number"]{-moz-appearance:textfield;appearance:textfield}
  .field input[type="number"]::-webkit-outer-spin-button,
  .field input[type="number"]::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
  .field select{appearance:none;cursor:pointer;padding-right:34px}
  .select-wrap{position:relative}
  .select-wrap::after{content:"";position:absolute;right:15px;top:50%;margin-top:-2px;
    border:4px solid transparent;border-top-color:var(--amber);pointer-events:none}

  /* A jade rail marks a field that holds an answer. "Filled" means it HAS
     characters, not that they are valid, so every filled rule stands down for
     the error state — without :not() a malformed email that is also focused
     would outrank the error and wear the success ring while wrong. */
  .field.is-filled:not(.is-invalid) input,
  .field.is-filled:not(.is-invalid) select,
  .field.is-filled:not(.is-invalid) textarea{
    box-shadow:inset 4px 0 0 var(--green),inset 0 0 0 1px var(--rim),0 1px 0 rgba(0,0,0,.5)}
  .field.is-filled:not(.is-invalid) input:focus,
  .field.is-filled:not(.is-invalid) select:focus,
  .field.is-filled:not(.is-invalid) textarea:focus{
    box-shadow:inset 4px 0 0 var(--green),inset 0 0 0 2px var(--green-lit),0 0 0 4px rgba(31,163,91,.18)}
  .field.is-invalid input,.field.is-invalid select,.field.is-invalid textarea{
    box-shadow:inset 4px 0 0 var(--ember),inset 0 0 0 1px #B4523C,0 1px 0 rgba(0,0,0,.5)}
  .field.is-invalid input:focus,.field.is-invalid select:focus,.field.is-invalid textarea:focus{
    box-shadow:inset 4px 0 0 var(--ember),inset 0 0 0 2px var(--ember),0 0 0 4px rgba(196,113,63,.18)}
  .err{display:none;font-family:var(--mono);font-size:10.5px;letter-spacing:.1em;
    color:var(--ember-lit);text-transform:uppercase}
  .field.is-invalid .err{display:block}

  /* Summary of what is wrong, above the fields. A field-level message is easy
     to miss on a long form, especially when the offending field is off-screen. */
  .form-err{display:none;margin-bottom:18px;padding:13px 15px;border-radius:0;
    background:rgba(196,113,63,.12);border:1px solid var(--ember);color:var(--ink);font-size:13.5px}
  .form-err.on{display:block}
  .form-err b{display:block;font-family:var(--mono);font-size:10.5px;letter-spacing:.16em;
    text-transform:uppercase;color:var(--ember-lit);margin-bottom:5px}
  .form-err ul{margin:0;padding-left:18px}
  .form-err li{margin:2px 0}
  .form-err button{background:none;border:0;padding:0;color:var(--ember-lit);cursor:pointer;
    font:inherit;text-decoration:underline;text-underline-offset:2px}

  .dlg-foot{display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;
    margin-top:22px;padding-top:20px;border-top:1px solid var(--line)}
  .dlg-foot p{margin:0;font-size:12.5px;color:var(--muted);max-width:34ch}
  .dlg-foot .btn{width:auto;padding:13px 22px}

  /* ---- confirmation ---- */
  .done{display:flex;flex-direction:column;gap:14px;padding:4px 0}
  .done-mark{width:46px;height:46px;border-radius:50%;background:var(--green);color:#FFFFFF;
    display:grid;place-items:center;font-size:22px;font-weight:700}
  .done-h{font-size:1.7rem;font-weight:300;letter-spacing:-.02em;margin:0 0 10px;line-height:1.1}
  .done-lede{margin:0;color:var(--muted);font-size:15px;max-width:48ch}
  .done-lede b{color:var(--ink);font-weight:600}
  /* Two columns so the receipt is half as tall — the confirmation used to run
     past the viewport and put a scrollbar on the scrim. */
  .summary{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--line);
    border:1px solid var(--line);border-radius:0;overflow:hidden;margin:0}
  .summary > div{background:var(--card-2);padding:10px 14px;display:flex;flex-direction:column;
    gap:2px;font-size:13.5px;min-width:0}
  .summary > div dd{overflow-wrap:anywhere}
  @media(max-width:620px){.summary{grid-template-columns:1fr}}
  .summary dt{font-family:var(--mono);font-size:10.5px;letter-spacing:.18em;text-transform:uppercase;color:var(--amber)}
  .summary dd{margin:0;text-align:right;color:var(--ink)}
  .done-next{border-top:1px solid var(--line);padding-top:18px;display:flex;flex-direction:column;gap:12px}
  .done-actions{display:flex;gap:10px;flex-wrap:wrap}
  .done-actions .btn{width:auto}
  .done-fine{margin:0;font-size:12.5px;color:var(--faint);max-width:52ch}

  @media(max-width:620px){
    .grid2{grid-template-columns:1fr}
    .dlg-head{padding:18px 20px 15px}
    .dlg-body{padding:18px 20px 22px}
    .dlg-title{font-size:1.35rem}
    .panel{right:12px;left:12px;width:auto}
  }
  @media(prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important}}
  `;

  var HTML = `
  <div id="stage" data-ready="0" data-open="0" data-stage>

    <div class="panel" id="alia-panel" role="dialog" aria-label="Three ways to reach ALIA">
      <p class="panel-t">Three ways in</p>
      <p class="panel-s">Which one are you here for?</p>
      <button class="btn btn-primary" type="button" data-act="corp">Corporate · Private events</button>
      <a class="btn btn-ghost" href="${CFG.openTableUrl}" target="_blank" rel="noopener noreferrer" data-act="rest">Restaurant · Book a table</a>
      <a class="btn btn-ghost" href="${CFG.phoneHref}" data-act="club">Club · Call the room</a>
      <p class="panel-note"><a href="${CFG.phoneHref}">${CFG.phone}</a></p>
    </div>

    <button class="fab" type="button" data-fab aria-expanded="false" aria-controls="alia-panel" aria-label="How to reach us">?</button>

    <div class="scrim" data-scrim>
      <div class="dlg" role="dialog" aria-modal="true" aria-labelledby="alia-dlg-title">
        <div class="dlg-head">
          <div>
            <p class="eyebrow">Private events</p>
            <h2 class="dlg-title" id="alia-dlg-title" data-dlgtitle>Request a date</h2>
            <p class="dlg-sub" data-dlgsub>Tell us the date and the headcount and we will come back to you.</p>
          </div>
          <button class="x" type="button" data-mclose aria-label="Close">&#10005;</button>
        </div>

        <div class="dlg-body">
          <div class="form-err" data-formerr role="alert" tabindex="-1" hidden></div>

          <form data-form novalidate>
            <div class="grid2">
              <div class="field">
                <label for="alia-f-name">Your name</label>
                <input id="alia-f-name" name="Name" type="text" autocomplete="name" placeholder="Alex Tremblay" required>
                <span class="err">Required</span>
              </div>
              <div class="field">
                <label for="alia-f-company">Company</label>
                <input id="alia-f-company" name="Company" type="text" autocomplete="organization" placeholder="Northbound Capital" required>
                <span class="err">Required</span>
              </div>
              <div class="field">
                <label for="alia-f-email">Work email</label>
                <input id="alia-f-email" name="Email" type="email" autocomplete="email" placeholder="alex@northbound.ca" required>
                <span class="err">Enter a valid email</span>
              </div>
              <div class="field">
                <label for="alia-f-phone">Phone</label>
                <input id="alia-f-phone" name="Phone" type="tel" autocomplete="tel" placeholder="514 000 0000" required>
                <span class="err">Required</span>
              </div>
              <div class="field">
                <label for="alia-f-event">Event</label>
                <div class="select-wrap">
                  <select id="alia-f-event" name="Event type" data-eventsel required>
                    <option value="" selected>Choose one</option>
                    <option>Year-end party</option>
                    <option>Client dinner</option>
                    <option>Full buyout</option>
                    <option>Cocktail reception</option>
                    <option>Product launch</option>
                    <option>Something else</option>
                  </select>
                </div>
                <span class="err">Required</span>
              </div>
              <div class="field">
                <label for="alia-f-guests">Guests</label>
                <input id="alia-f-guests" name="Guests" type="number" min="1" max="400" inputmode="numeric" placeholder="60" required>
                <span class="err">Required</span>
              </div>
              <div class="field span2" data-otherwrap hidden>
                <label for="alia-f-other">Tell us what you have in mind</label>
                <input id="alia-f-other" name="Event detail" type="text" placeholder="A short description">
                <span class="err">Required</span>
              </div>
              <div class="field">
                <label for="alia-f-date">Preferred date</label>
                <input id="alia-f-date" name="Preferred date" type="date" required>
                <span class="err">Required</span>
              </div>
              <div class="field">
                <label for="alia-f-budget">Budget per head <span class="opt">(optional)</span></label>
                <div class="select-wrap">
                  <select id="alia-f-budget" name="Budget per head">
                    <option value="" selected>Not sure yet</option>
                    <option>$95 to $150</option>
                    <option>$150 to $225</option>
                    <option>$225 and up</option>
                  </select>
                </div>
              </div>
              <div class="field span2">
                <label for="alia-f-notes">Anything we should know <span class="opt">(optional)</span></label>
                <textarea id="alia-f-notes" name="Notes" placeholder="Dietary needs, AV, a speech, a hard end time."></textarea>
              </div>
            </div>
            <div class="dlg-foot">
              <p>We will confirm availability by email or phone.</p>
              <button class="btn btn-primary" type="submit" data-submit>Send request</button>
            </div>
          </form>

          <div class="done" data-done hidden aria-live="polite">
            <div class="done-mark" aria-hidden="true">&#10003;</div>
            <div>
              <h3 class="done-h" data-donetitle>Thank you.</h3>
              <p class="done-lede" data-donelede></p>
            </div>
            <dl class="summary" data-summary></dl>
            <div class="done-next">
              <p class="eyebrow">If you need us sooner</p>
              <div class="done-actions">
                <a class="btn btn-ghost" href="${CFG.phoneHref}">Call ${CFG.phone}</a>
                <button class="btn btn-ghost" type="button" data-again hidden>Send another request</button>
              </div>
              <p class="done-fine" data-donefine></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  `;

  var host = document.createElement('div');
  host.id = 'alia-wgt-host';
  document.body.appendChild(host);
  var root = host.attachShadow({ mode: 'open' });
  root.innerHTML = '<style>' + CSS + '</style>' + HTML;

  /* The theme ships its own Reserve/Call dock pinned to the same corner. The
     widget supersedes it. Injected from JS on purpose: if this script fails to
     load, the theme's dock survives as the fallback. */
  var hideDock = document.createElement('style');
  hideDock.id = 'alia-wgt-dock-hide';
  hideDock.textContent = '.alia-cta-dock{display:none!important}';
  document.head.appendChild(hideDock);

  var stage    = root.querySelector('[data-stage]');
  var fab      = root.querySelector('[data-fab]');
  var scrim    = root.querySelector('[data-scrim]');
  var form     = root.querySelector('[data-form]');
  var done     = root.querySelector('[data-done]');
  var summary  = root.querySelector('[data-summary]');
  var submitBtn= root.querySelector('[data-submit]');
  var againBtn = root.querySelector('[data-again]');
  var formErr  = root.querySelector('[data-formerr]');
  var eventSel = root.querySelector('[data-eventsel]');
  var otherWrap= root.querySelector('[data-otherwrap]');
  var otherIn  = root.querySelector('#alia-f-other');
  var lastFocus;

  /* ---------------- panel ---------------- */
  function panelOpen() { return stage.getAttribute('data-open') === '1'; }
  function setPanel(open) {
    stage.setAttribute('data-open', open ? '1' : '0');
    fab.setAttribute('aria-expanded', String(open));
    fab.textContent = open ? '✕' : '?';
  }
  fab.addEventListener('click', function () { setPanel(!panelOpen()); });

  /* ---------------- session guard ----------------
     After a successful send, remember what was sent for the rest of the tab
     session. Reopening then replays the confirmation — what was sent, and a
     deliberate "Send another request" — rather than a primed blank form, which
     is the obvious way to end up with the same lead in the sheet twice.

     A guard, not a lockout: the way back to a fresh form is one click away, it
     just has to be asked for.

     sessionStorage, not localStorage: this should last the visit, not follow
     someone around for weeks. Wrapped in try/catch because private-mode
     Safari throws on write — a failed write means no guard, never a broken
     form. */
  var GUARD_KEY = 'alia-corp-sent';

  function guardSave(payload) {
    try {
      sessionStorage.setItem(GUARD_KEY, JSON.stringify({ data: payload.data }));
    } catch (err) { /* private mode — the guard is a nicety, not a requirement */ }
  }
  /* Returns a record only if it can actually be replayed. A key left behind by
     an older build, or half-written, must fall through to a normal blank form
     rather than open a confirmation with nothing in it. */
  function guardLoad() {
    try {
      var raw = sessionStorage.getItem(GUARD_KEY);
      var rec = raw ? JSON.parse(raw) : null;
      if (!rec || !rec.data || typeof rec.data !== 'object') return null;
      return rec;
    } catch (err) { return null; }
  }
  function guardClear() {
    try { sessionStorage.removeItem(GUARD_KEY); } catch (err) {}
  }

  /* ---------------- dialog ---------------- */
  function openModal() {
    setPanel(false);
    lastFocus = document.activeElement;
    clearFormError();

    scrim.classList.add('on');
    lockPageScroll(true);

    /* Already sent in this session? Replay what was sent instead of a primed
       form. The stored copy is the source of truth, not whatever is still
       sitting in the form fields — a page navigation between the send and the
       reopen leaves the form blank but the guard intact. */
    var prior = guardLoad();
    if (prior) {
      showConfirmation({ data: prior.data }, true, true);
      againBtn.focus({ preventScroll: true });
      return;
    }

    // a second enquiry must not open onto the previous one's confirmation
    form.hidden = false;
    done.hidden = true;
    root.querySelector('[data-dlgtitle]').textContent = 'Request a date';
    root.querySelector('[data-dlgsub]').textContent =
      'Tell us the date and the headcount and we will come back to you.';

    var first = root.querySelector('#alia-f-name');
    if (first) first.focus({ preventScroll: true });
  }
  function closeModal() {
    scrim.classList.remove('on');
    lockPageScroll(false);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* Freeze the page behind the dialog. Without this the page keeps its own
     scrollbar alongside the scrim's, which reads as two scrollbars, and the
     page silently scrolls under the overlay when the guest spins the wheel.
     The scrollbar's width is replaced as padding so the layout does not jump
     sideways the moment the dialog opens. */
  var scrollLocked = false, priorOverflow = '', priorPadding = '';
  function lockPageScroll(on) {
    if (on === scrollLocked) return;
    var b = document.body;
    if (on) {
      priorOverflow = b.style.overflow;
      priorPadding = b.style.paddingRight;
      var gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) b.style.paddingRight = gap + 'px';
      b.style.overflow = 'hidden';
    } else {
      b.style.overflow = priorOverflow;
      b.style.paddingRight = priorPadding;
    }
    scrollLocked = on;
  }
  root.querySelector('[data-mclose]').addEventListener('click', closeModal);
  scrim.addEventListener('click', function (e) { if (e.target === scrim) closeModal(); });

  /* TRAP A ---------------------------------------------------------------
     Shadow DOM is a boundary in BOTH directions: a listener on the shadow
     root never sees data-act="corp" buttons out on the page. Bind to
     document and walk composedPath(), which contains both the page nodes and
     the shadow nodes for any click — one handler, no double-firing.
     Restaurant and Club are real <a> elements everywhere, so they are left
     to the browser and deliberately not intercepted here. */
  document.addEventListener('click', function (e) {
    var path = (e.composedPath && e.composedPath()) || [];
    for (var i = 0; i < path.length; i++) {
      var n = path[i];
      if (!n || n.nodeType !== 1) continue;
      if (n.getAttribute && n.getAttribute('data-act') === 'corp') {
        e.preventDefault();
        openModal();
        return;
      }
    }
    // click anywhere else closes the panel
    if (panelOpen() && e.target !== host && !host.contains(e.target)) setPanel(false);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (scrim.classList.contains('on')) closeModal();
    else if (panelOpen()) { setPanel(false); fab.focus(); }
  });

  /* ---------------- conditional "Something else" ----------------
     TRAP H: a required control that is display:none can never be focused, so
     checkValidity() fails with nothing on screen to explain why. required is
     added and removed with the visibility, never left on a hidden field. */
  function syncOther() {
    var on = eventSel.value === 'Something else';
    otherWrap.hidden = !on;
    if (on) {
      otherIn.setAttribute('required', '');
    } else {
      otherIn.removeAttribute('required');
      otherIn.value = '';
      otherWrap.classList.remove('is-filled', 'is-invalid');
      delete otherWrap.dataset.touched;
    }
  }
  eventSel.addEventListener('change', syncOther);
  syncOther();

  /* ---------------- field state ----------------
     State is written on the .field WRAPPER rather than read from competing
     pseudo-classes on the control, so every CSS rule sits at one specificity
     tier and the order in the stylesheet IS the priority order. It also
     sidesteps :placeholder-shown, which never reports a date input or an
     unchosen <select> as empty. */
  var fields = [].slice.call(form.querySelectorAll('.field'));
  function controlOf(f) { return f.querySelector('input, select, textarea'); }
  /* Specific messages. "Required" on nine fields tells nobody which nine, and
     the browser's own strings are inconsistent across engines. Derived from
     the ValidityState so the message always matches the actual failure. */
  function problemWith(c) {
    var v = c.validity, label = labelFor(c);
    if (v.valid) return null;
    if (v.valueMissing) {
      if (c.tagName === 'SELECT') return 'Choose an ' + label.toLowerCase();
      return label + ' is required';
    }
    if (v.typeMismatch && c.type === 'email') return 'That email address does not look right';
    if (v.rangeUnderflow) return 'Guests must be at least ' + c.min;
    if (v.rangeOverflow) return 'For more than ' + c.max + ' guests, please call us';
    if (v.badInput && c.type === 'number') return 'Guests must be a number';
    if (v.badInput && c.type === 'date') return 'That date is not valid';
    return label + ' is not valid';
  }

  function labelFor(c) {
    var f = c.closest('.field');
    var l = f && f.querySelector('label');
    if (!l) return 'This field';
    /* strip the "(optional)" suffix so messages read cleanly */
    return l.textContent.replace(/\(optional\)/i, '').trim();
  }

  function syncField(f) {
    var c = controlOf(f);
    if (!c) return;
    f.classList.toggle('is-filled', String(c.value || '').trim() !== '');
    if (f.dataset.touched === '1') {
      var ok = c.checkValidity();
      f.classList.toggle('is-invalid', !ok);
      c.setAttribute('aria-invalid', String(!ok));
      if (!ok) {
        var msg = problemWith(c);
        var slot = f.querySelector('.err');
        if (slot && msg) slot.textContent = msg;
      }
    }
  }

  /* ---------------- error summary ---------------- */
  function clearFormError() {
    formErr.hidden = true;
    formErr.classList.remove('on');
    formErr.innerHTML = '';
  }

  function showFormError(problems) {
    formErr.innerHTML = '';
    var b = document.createElement('b');
    b.textContent = problems.length === 1
      ? 'One thing needs fixing'
      : problems.length + ' things need fixing';
    formErr.appendChild(b);
    var ul = document.createElement('ul');
    problems.forEach(function (p) {
      var li = document.createElement('li');
      var jump = document.createElement('button');
      jump.type = 'button';
      jump.textContent = p.msg;
      /* clicking a summary line takes you to the field it is about */
      jump.addEventListener('click', function () {
        p.control.focus({ preventScroll: true });
        p.control.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      li.appendChild(jump);
      ul.appendChild(li);
    });
    formErr.appendChild(ul);
    formErr.hidden = false;
    formErr.classList.add('on');
    formErr.focus({ preventScroll: true });
    formErr.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /* ---------------- send another request ----------------
     The deliberate way back to a blank form, for a genuinely different
     enquiry. Clearing the guard is the point: without it the next reopen would
     replay a confirmation for a request that has just been superseded. */
  againBtn.addEventListener('click', function () {
    guardClear();
    form.reset();
    fields.forEach(function (f) {
      f.classList.remove('is-filled', 'is-invalid');
      delete f.dataset.touched;
    });
    syncOther();
    syncAll();
    clearFormError();
    done.hidden = true;
    form.hidden = false;
    root.querySelector('[data-dlgtitle]').textContent = 'Request a date';
    root.querySelector('[data-dlgsub]').textContent =
      'Tell us the date and the headcount and we will come back to you.';
    var first = root.querySelector('#alia-f-name');
    if (first) first.focus({ preventScroll: true });
  });
  function syncAll() { fields.forEach(syncField); }
  fields.forEach(function (f) {
    var c = controlOf(f);
    if (!c) return;
    c.addEventListener('input', function () { syncField(f); });
    c.addEventListener('change', function () { syncField(f); });
    c.addEventListener('blur', function () { f.dataset.touched = '1'; syncField(f); });
  });
  syncAll();

  // a date in the past helps nobody
  root.querySelector('#alia-f-date').min = new Date().toISOString().slice(0, 10);

  /* the date is stored ISO but read back the way a person says it out loud */
  function niceDate(iso) {
    try {
      var p = String(iso).split('-');
      return new Date(p[0], p[1] - 1, p[2]).toLocaleDateString('en-CA',
        { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } catch (err) { return iso; }
  }

  /* The on-screen receipt, in the order it reads back. This list controls the
     confirmation only — collect() sends every non-empty field to the sheet
     regardless of what appears here.

     An explicit list rather than a walk of form.elements: it fixes the reading
     order independently of the DOM order the layout happens to want, and it
     keeps Notes out. Notes is free text of any length, and the receipt's cells
     are right-aligned two-up — a paragraph in one reads as a layout fault. */
  var RECEIPT = [
    'Name', 'Company', 'Email', 'Phone',
    'Event type', 'Event detail', 'Guests', 'Preferred date', 'Budget per head'
  ];

  /* Built from a data object, not from the live form. The returning view has to
     redraw this from the stored copy, and by then a page navigation may well
     have left the form fields empty. */
  function renderSummary(data) {
    summary.innerHTML = '';
    RECEIPT.forEach(function (name) {
      var v = data[name];
      if (!v) return;
      var row = document.createElement('div');
      var dt = document.createElement('dt'); dt.textContent = name;
      var dd = document.createElement('dd');
      dd.textContent = name === 'Preferred date' ? niceDate(v) : v;
      row.appendChild(dt); row.appendChild(dd);
      summary.appendChild(row);
    });
  }

  function collect() {
    var data = {}, lines = [];
    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || !el.value) return;
      data[el.name] = el.value;
      lines.push(el.name + ': ' + (el.name === 'Preferred date' ? niceDate(el.value) : el.value));
    });
    return { data: data, text: lines.join('\n') };
  }

  /* TRAP B -----------------------------------------------------------------
     The old signature was showConfirmation(p, viaEmail) — an INVERTED flag.
     The fetch success branch passed false and both failure branches passed
     true, because the parameter meant "we fell back to email", not
     "delivered". Renamed and the polarity flipped: `delivered` is true only on
     the success path, so a lead that posted cleanly never tells the guest we
     could not submit it.

     TRAP C: an Apps Script response is opaque — resolving proves the request
     left the browser, not that a row was written. The Leads sheet is the only
     proof of delivery. The copy below is worded to reflect that. */
  function showConfirmation(p, delivered, returning) {
    /* Only a send that actually left the browser is worth guarding against a
       duplicate. Remembering a failed one would replay "already sent" over a
       lead that never arrived — and the phone number is the recovery path, not
       a locked form. `returning` is already reading from the guard, so it has
       nothing to write back. */
    if (delivered && !returning) guardSave(p);
    clearFormError();
    renderSummary(p.data);
    var first = String(p.data.Name || '').trim().split(/\s+/)[0];

    root.querySelector('[data-dlgtitle]').textContent =
      returning ? 'Already sent' : delivered ? 'Request received' : 'One more step';
    root.querySelector('[data-dlgsub]').textContent =
      returning ? 'We have this one.'
      : delivered ? 'It is with our events team.'
      : 'We could not submit this from your browser.';

    root.querySelector('[data-donetitle]').textContent = delivered
      ? (first ? 'Thank you, ' + first + '.' : 'Thank you.')
      : 'Please give us a call.';

    root.querySelector('[data-donelede]').innerHTML = delivered
      ? 'Your request for <b>' + (p.data.Guests || '') + ' guests</b> on <b>' +
        niceDate(p.data['Preferred date'] || '') + '</b> is with our events team. ' +
        'We will confirm availability with you directly.'
      : 'Your request for <b>' + (p.data.Guests || '') + ' guests</b> on <b>' +
        niceDate(p.data['Preferred date'] || '') + '</b> did not reach us. ' +
        'Call <b>' + CFG.phone + '</b> and we will take the details over the phone.';

    root.querySelector('[data-donefine]').textContent =
      returning ? 'Sent from this device. Use the button below if you need us for a different date or headcount.'
      : delivered ? 'We have your details. If your plans change before you hear back, call us.'
      : 'Nothing was lost on your side — the details are still in the form behind this message.';

    /* A fresh form is only worth offering once something has actually been
       sent. On the failure path the form still holds the answers, and wiping
       them to start over is the opposite of what is needed. */
    againBtn.hidden = !(delivered || returning);

    form.hidden = true;
    done.hidden = false;
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    fields.forEach(function (f) { if (!f.hidden) f.dataset.touched = '1'; });
    syncAll();
    clearFormError();

    if (!form.checkValidity()) {
      /* Collect every problem, not just the first, so the guest fixes the form
         in one pass instead of discovering faults one submit at a time. */
      var problems = [];
      fields.forEach(function (f) {
        if (f.hidden) return;
        var c = controlOf(f);
        if (!c || c.checkValidity()) return;
        problems.push({ control: c, msg: problemWith(c) });
      });
      showFormError(problems);
      if (problems.length) problems[0].control.focus({ preventScroll: true });
      return;
    }

    var payload = collect();

    /* Plumbing, not the guest's answers. Both are columns in the Leads sheet
       and neither is a form input — Site identifies which of the ALIA sites
       the lead came from, Page which URL they were on. */
    payload.data.Site = CFG.site;
    payload.data.Page = location.href;

    /* No endpoint means the form genuinely cannot deliver. Say so and point at
       the phone — there is deliberately no mailto fallback anywhere on this
       site. */
    if (!CFG.endpoint) {
      showConfirmation(payload, false);
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending';

    /* text/plain is deliberate: it keeps this a "simple request" so the
       browser skips the CORS preflight, which Apps Script rejects outright.
       Do not change this to application/json. */
    fetch(CFG.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload.data)
    }).then(function () {
      showConfirmation(payload, true);
    }).catch(function () {
      showConfirmation(payload, false);
    }).then(function () {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send request';
    });
  });

  /* ---------------- hold the launcher back until the splash clears ----------
     The theme fades #pre out rather than removing it and fires no event when
     it finishes, so watch the element: a MutationObserver catches the class
     flip, a poll covers the opacity transition, and a timeout guarantees the
     launcher appears even if the intro script throws. First one wins. */
  function introGone(el) {
    if (!el || !el.parentNode) return true;
    /* The theme marks the intro finished by adding .gone and THEN fades #pre
       out over .7s. Waiting for computed opacity to fall below .1 meant the
       MutationObserver fired the moment .gone landed, this test said "not
       yet", and the launcher only appeared at the END of the fade — about a
       second after the splash had visually cleared.
       Treat the dismissal class itself as done, so the launcher rises with
       the splash rather than after it. */
    if (el.classList && (el.classList.contains('gone') ||
                         el.classList.contains('done') ||
                         el.classList.contains('is-hidden') ||
                         el.classList.contains('hidden'))) return true;
    var cs = getComputedStyle(el);
    /* .5 rather than .1: for a theme with no dismissal class, fire once the
       splash is halfway out instead of fully gone. */
    return cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.5;
  }
  function reveal() { stage.setAttribute('data-ready', '1'); }

  /* Find the real preloader element.
     NOT document.querySelector(selector) — that returns the first match in
     DOCUMENT ORDER, and after the Route B conversion the preloader lives
     inside a Shopify section wrapper whose id ends in "__preloader". That
     wrapper matches [id*="preload"] and, being #pre's PARENT, comes first.
     The wrapper never receives .gone, so the observer and the poll both
     watched an element that could never change and the launcher only appeared
     on the 9s safety timeout.
     Skip section wrappers, then prefer the innermost remaining candidate. */
  function findPreloader() {
    if (!CFG.preloaderSelector) return null;
    var all = [].slice.call(document.querySelectorAll(CFG.preloaderSelector))
      .filter(function (el) { return !el.classList.contains('shopify-section'); });
    if (!all.length) return null;
    var innermost = all.filter(function (el) {
      return !all.some(function (other) { return other !== el && el.contains(other); });
    });
    return innermost[0] || all[0];
  }

  /* ------------------------------------------------------------------
     NAV SHRINK ON NON-HOMEPAGE TEMPLATES (blog, article, page, etc.)
     The homepage's 03-golden-hour.js drives #nav.shrunk via GSAP
     ScrollTrigger + #hero. On any template without #hero that code never
     runs, so the nav stays transparent even when scrolled. This listener
     replicates the same behaviour with a plain scroll event — it only
     activates when #hero is absent so it can never double-fire on the
     home page.
     ------------------------------------------------------------------ */
  (function initNavShrink() {
    if (document.getElementById('hero')) return; // homepage handles its own nav
    var nav = document.getElementById('nav');
    if (!nav) return;
    var THRESHOLD = 80; // px — match the visual feel of the hero trigger
    function onScroll() {
      if (window.scrollY > THRESHOLD) {
        nav.classList.add('shrunk');
      } else {
        nav.classList.remove('shrunk');
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on load in case the page is already scrolled
  })();

  /* ------------------------------------------------------------------
     MOBILE DRAWER ON NON-HOMEPAGE TEMPLATES (blog, article, page, etc.)
     03-golden-hour.js calls buildDrawer() as part of boot(), but boot()
     only runs on pages that have the full theme motion system. On blog
     and article pages it never fires, so the burger button is inert.
     This replicates the exact same logic — same data attributes, same
     CSS classes — and skips itself when #hero is present so it can
     never double-bind on the homepage.
     ------------------------------------------------------------------ */
  (function initDrawer() {
    if (document.getElementById('hero')) return; // 03-golden-hour.js handles it
    var burger = document.querySelector('[data-drawer-toggle]');
    var drawer = document.querySelector('[data-drawer]');
    var scrim  = document.querySelector('[data-drawer-scrim]');
    var closeB = document.querySelector('[data-drawer-close]');
    if (!burger || !drawer || !scrim) return;

    var lastFocus = null;
    var priorOverflow = '';

    function isOpen() { return drawer.classList.contains('is-open'); }

    function setOpen(open) {
      drawer.classList.toggle('is-open', open);
      scrim.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', String(open));
      if (open) {
        lastFocus = document.activeElement;
        priorOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        var first = drawer.querySelector('a, button');
        if (first) first.focus({ preventScroll: true });
      } else {
        document.body.style.overflow = priorOverflow;
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      }
    }

    burger.addEventListener('click', function () { setOpen(!isOpen()); });
    scrim.addEventListener('click',  function () { setOpen(false); });
    if (closeB) closeB.addEventListener('click', function () { setOpen(false); });

    /* Any nav link inside the drawer closes it */
    Array.prototype.forEach.call(drawer.querySelectorAll('a'), function (a) {
      a.addEventListener('click', function () { setOpen(false); });
    });
    /* Corporate button opens the lead form — drawer must get out of the way */
    Array.prototype.forEach.call(drawer.querySelectorAll('[data-act="corp"]'), function (b) {
      b.addEventListener('click', function () { setOpen(false); });
    });

    /* Escape key closes */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) setOpen(false);
    });

    /* Crossing the 821px breakpoint with drawer open would leave the page
       scroll-locked with nothing visible — close it */
    var mq = window.matchMedia('(min-width: 821px)');
    function onWide(e) { if (e.matches && isOpen()) setOpen(false); }
    if (mq.addEventListener) { mq.addEventListener('change', onWide); }
    else if (mq.addListener)  { mq.addListener(onWide); } // Safari < 14 fallback
  })();

  (function whenIntroDone() {
    var pre = findPreloader();
    if (introGone(pre)) { reveal(); return; }
    var fired = false, obs = null, poll, safety;
    function finish() {
      if (fired) return;
      fired = true;
      if (obs) obs.disconnect();
      clearInterval(poll);
      clearTimeout(safety);
      setTimeout(reveal, CFG.revealDelay);
    }
    function check() { if (introGone(findPreloader())) finish(); }
    if (window.MutationObserver) {
      obs = new MutationObserver(check);
      obs.observe(pre, { attributes: true, attributeFilter: ['class', 'style'] });
      if (pre.parentNode) obs.observe(pre.parentNode, { childList: true });
    }
    /* The observer is the fast path; this poll is the backstop for a splash
       that fades without any attribute changing. 80ms keeps its worst case
       below one animation frame's worth of perceptible lag. */
    poll = setInterval(check, 80);
    safety = setTimeout(finish, CFG.revealTimeout);
  })();
})();
